import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAppSettings } from "../../../../hooks/useAppSettings.tsx";
import Layout from "../../../Layout.tsx";
import Title from "../../../Title.tsx";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/index.ts";
import { useFormik } from "formik";
import moment from "moment";
import { weeks } from "../../../../utils/constanst.js";
import CustomSelectField from "../../../formik/CustomSelectField.tsx";
import ConfigHorarioSemanalFilters from "./ConfigHorarioSemanalFilters.tsx";
import ConfigHorarioSemanalTable from "./ConfigHorarioSemanalTable.tsx";
import ConfigHorarioSemanalExportModal from "./ConfigHorarioSemanalExportModal.tsx";
import Loader from "../../../Loader.tsx";
import {
  readPositionsByBranchAndShift,
  readUniquePositionNamesByBranch,
  readSchedulesByBranchAndWeek,
  readHoursByBranch,
  saveSchedule,
} from "../../../../services/branchSchedule.service.ts";
import { readPartnersByBranch } from "../../../../services/partner.service.ts";
import { readBranch } from "../../../../services/branch.service.ts";
import {
  saveBranchScheduleWeekend,
  findBranchScheduleWeekendByBranchAndWeek,
  updateBranchScheduleWeekend,
} from "../../../../services/branchScheduleWeekend.service.ts";
import { notification } from "../../../../utils/Notifications.tsx";
import { getDateByDayOfWeek } from "../../../../utils/constanst.js";
import { fetchFreeDaysByDateRangeAndPartnerIds } from "../../../../services/freeDay.service.ts";
import {
  normalizePositionIdKey,
  dedupePartnerDayShiftPositionItems,
  BRANCH_SCHEDULES_SAVED_EVENT,
  notifyBranchSchedulesSaved,
} from "./scheduleKeyUtils.ts";

function normalizeShiftNameForSave(shift: any): string {
  if (shift == null || shift === "") return "";
  if (typeof shift === "string") return shift.trim();
  if (typeof shift === "object" && (shift as { name?: string }).name != null) {
    return String((shift as { name: string }).name).trim();
  }
  return String(shift).trim();
}

/** Alinea nombres de plaza (SUC 1 vs SUC 2, espacios) para emparejar desired vs BranchSchedule. */
function normalizePositionLabelForSave(value: string): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function dedupeScheduleRecordsForSave(records: any[]): any[] {
  const seenOriginal = new Set<number>();
  const seenNew = new Set<string>();
  const out: any[] = [];
  for (const r of records) {
    const oid = r?.originalRecordId != null ? Number(r.originalRecordId) : NaN;
    if (!Number.isNaN(oid) && oid > 0) {
      if (seenOriginal.has(oid)) continue;
      seenOriginal.add(oid);
      out.push(r);
      continue;
    }
    const nk = `${r.partner}|${r.dayOfWeek}|${normalizeShiftNameForSave(r.shift)}|${r.position}|${normalizePositionLabelForSave(String(r.positionName ?? ""))}`;
    if (seenNew.has(nk)) continue;
    seenNew.add(nk);
    out.push(r);
  }
  return out;
}

const ConfigHorarioSemanal = () => {
  useAppSettings();

  const { user } = useSelector((state: RootState) => state.auth);
  const { id: ipParams } = useParams();

  let id: number;
  if (user.rol === "Lider de sucursal") {
    id = user.branchId;
  } else {
    id = Number(ipParams);
  }

  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [positions, setPositions] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [partners, setPartners] = useState<any[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<number>(id);
  const [branchName, setBranchName] = useState<string>("");
  const [showExportModal, setShowExportModal] = useState(false);
  const tableRef = useRef<{
    getTableData: () => any;
    fillWeekWithCurrentDay: () => boolean;
    syncWeekWithSchedule: (schedules: any[]) => boolean;
    exportCurrentTableAsPdf: (options: { branchName: string; weekName: string; notes?: string }) => Promise<void>;
    getWeeklySaveContext: () => {
      cellAssignments: Record<string, string>;
      initialCellAssignments: Record<string, string>;
      removedScheduleIds: number[];
    };
  }>(null);
  const [saving, setSaving] = useState(false);
  const [exportingTablePdf, setExportingTablePdf] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [freeDays, setFreeDays] = useState<any[]>([]);
  const [weekendSchedule, setWeekendSchedule] = useState<any>(null);
  const [branchHours, setBranchHours] = useState<any | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [weekNotes, setWeekNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const formik = useFormik({
    initialValues: {
      week: 1,
    },
    onSubmit: (values) => {},
  });

  const fetchDependencies = useCallback(
    async (branchId: number, weekId: number) => {
      if (weekId === undefined || weekId === null || !branchId) {
        return;
      }

      const weekData = weeks[weekId];
      if (!weekData || !weekData.value) {
        console.warn(`Semana con ID ${weekId} no encontrada o sin valor`);
        return;
      }

      const weekValue = weekData.value;

      setLoading(true);
      try {
        const positionsByShift = await Promise.all([
          readPositionsByBranchAndShift(branchId, "Matutino"),
          readPositionsByBranchAndShift(branchId, "Intermedio"),
          readPositionsByBranchAndShift(branchId, "Vespertino"),
        ]);

        const formattedPositions: any[] = [];

        ["Matutino", "Intermedio", "Vespertino"].forEach(
          (shift, shiftIndex) => {
            const shiftPositions = (positionsByShift[shiftIndex] || [])
              .slice()
              .sort((a: any, b: any) =>
                a.position.name.localeCompare(b.position.name, "es"),
              );

            shiftPositions.forEach((pos: any) => {
              const n = Math.max(0, Number(pos.numberOfWorkers) || 0);
              const isEssencial = Boolean(pos.isEssencial);
              if (n > 0) {
                for (let i = 0; i < n; i++) {
                  formattedPositions.push({
                    id: `${pos.position.id}-${shift}-${i}`,
                    positionId: pos.position.id,
                    positionName: pos.position.name,
                    positionDisplayName: `${pos.position.name} ${i + 1}`,
                    positionOrder: Number(pos.position.order) || 0,
                    shift: shift,
                    numberOfWorkers: n,
                    showSchedule: pos.showSchedule,
                    isEssencial,
                  });
                }
              } else {
                // Incluir puesto en plantilla aunque tenga 0 trabajadores (coincide con /branch-schedule/positions/branch/:id)
                formattedPositions.push({
                  id: `${pos.position.id}-${shift}-0`,
                  positionId: pos.position.id,
                  positionName: pos.position.name,
                  positionDisplayName: pos.position.name,
                  positionOrder: Number(pos.position.order) || 0,
                  shift: shift,
                  numberOfWorkers: 0,
                  showSchedule: pos.showSchedule,
                  isEssencial,
                });
              }
            });
          },
        );

        setPositions(
          formattedPositions.filter((position) => position.showSchedule),
        );

        const branchPartners = await readPartnersByBranch(branchId, {
          active: true,
        });
        setPartners(branchPartners);

        const weekendScheduleData =
          await findBranchScheduleWeekendByBranchAndWeek(branchId, weekValue);
        setWeekendSchedule(weekendScheduleData || null);

        const partnerIds = branchPartners.map((p: any) => p.id);
        const freeDaysData =
          partnerIds.length > 0
            ? await fetchFreeDaysByDateRangeAndPartnerIds(weekValue, partnerIds)
            : [];
        setFreeDays(freeDaysData || []);

        const scheduleData = await readSchedulesByBranchAndWeek(
          branchId,
          weekValue,
        );
        setSchedules(Array.isArray(scheduleData) ? scheduleData : []);

        const uniquePositionNames =
          await readUniquePositionNamesByBranch(branchId);
        setAvailableRoles(uniquePositionNames);

        const branchData = await readBranch(branchId);
        setBranchName(branchData?.name || "");

        const hoursData = await readHoursByBranch(branchId);
        setBranchHours(hoursData || null);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (id) {
      setSelectedBranchId(id);
    }
  }, [id]);

  useEffect(() => {
    if (
      selectedBranchId &&
      formik.values.week !== undefined &&
      formik.values.week !== null
    ) {
      fetchDependencies(selectedBranchId, formik.values.week);
    }
  }, [
    location.key,
    location.pathname,
    selectedBranchId,
    formik.values.week,
    fetchDependencies,
  ]);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{
        branchId?: number;
        weekIndex?: number;
      }>;
      const bid = ce.detail?.branchId;
      if (bid == null || Number(bid) !== Number(selectedBranchId)) return;
      const savedWeek = ce.detail?.weekIndex;
      // Comparar como número: el horario individual a veces manda string desde Formik.
      if (
        savedWeek !== undefined &&
        savedWeek !== null &&
        Number(savedWeek) !== Number(formik.values.week)
      ) {
        return;
      }
      fetchDependencies(selectedBranchId, formik.values.week);
    };
    window.addEventListener(BRANCH_SCHEDULES_SAVED_EVENT, handler);
    return () =>
      window.removeEventListener(BRANCH_SCHEDULES_SAVED_EVENT, handler);
  }, [selectedBranchId, formik.values.week, fetchDependencies]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const weekValue = weeks[formik.values.week]?.value;
      if (!weekValue) {
        notification("Error", "Semana no válida", "danger");
        return;
      }

      const weekStart = getDateByDayOfWeek(weekValue, "Lunes");
      const weekEnd = getDateByDayOfWeek(weekValue, "Domingo");

      if (!weekStart || !weekEnd) {
        notification(
          "Error",
          "Error al obtener las fechas de la semana",
          "danger",
        );
        return;
      }

      if (!tableRef.current) {
        notification(
          "Error",
          "No se pudo obtener los datos de la tabla",
          "danger",
        );
        return;
      }

      const tableData = tableRef.current.getTableData();
      const { items, restDays } = tableData;
      const weeklyCtx = tableRef.current.getWeeklySaveContext();
      const removedScheduleIds = new Set<number>(
        (weeklyCtx.removedScheduleIds || []).map((id: number) => Number(id)),
      );

      // Estado de la semana en React puede estar desactualizado (p. ej. cambios en horario individual).
      // Fundir siempre contra lo que hay ahora en API para no reinsertar asignaciones ya eliminadas.
      const freshSchedules = await readSchedulesByBranchAndWeek(
        selectedBranchId,
        weekValue,
      );
      const schedulesForSave = Array.isArray(freshSchedules) ? freshSchedules : [];

      await saveBranchScheduleWeekend({
        branchId: selectedBranchId,
        weekValue: weekValue,
        weekStart: moment(weekStart).format("YYYY-MM-DD"),
        weekEnd: moment(weekEnd).format("YYYY-MM-DD"),
        items: items,
        restDays: restDays,
      });

      // Guardar en el horario normal (check-schedule) como SheduleComponent: por día y por turno, con el horario del shift
      const shiftToNum: Record<string, number> = {
        Matutino: 0,
        Intermedio: 1,
        Vespertino: 2,
      };
      const shiftNames = ["Matutino", "Intermedio", "Vespertino"];
      const formatTime = (t: string) =>
        String(t ?? "").trim().length >= 5
          ? String(t).trim().slice(0, 5)
          : String(t ?? "").trim();

      const getStartEndForShift = (shiftName: string) => {
        if (!branchHours) return { startTime: "", endTime: "" };
        if (shiftName === "Matutino")
          return {
            startTime: formatTime(branchHours.matutinoStart),
            endTime: formatTime(branchHours.matutinoEnd),
          };
        if (shiftName === "Intermedio")
          return {
            startTime: formatTime(branchHours.intermedioStart),
            endTime: formatTime(branchHours.intermedioEnd),
          };
        if (shiftName === "Vespertino")
          return {
            startTime: formatTime(branchHours.vespertinoStart),
            endTime: formatTime(branchHours.vespertinoEnd),
          };
        return { startTime: "", endTime: "" };
      };

      // Grid del horario: cada columna = 30 min; grid desde matutinoStart hasta vespertinoEnd (como SheduleComponent)
      const timeToMinutes = (s: string) => {
        const t = String(s ?? "").trim().slice(0, 5);
        const [h, m] = t.split(":").map(Number);
        return (h || 0) * 60 + (m || 0);
      };
      const parseStartEnd = (rangeStr: string) => {
        if (String(rangeStr || "").includes(" - ")) {
          const parts = rangeStr.trim().split(" - ");
          return {
            start: timeToMinutes(parts[0]),
            end: timeToMinutes(parts[1] ?? parts[0]),
          };
        }
        return { start: 0, end: 0 };
      };
      const getPositionXW = (
        startEndStr: string,
        shiftStart: string,
        shiftEnd: string,
      ) => {
        if (!branchHours) return { positionX: 0, positionW: 1 };
        const gridStartMin = timeToMinutes(branchHours.matutinoStart);
        const { start: startMin, end: endMin } = parseStartEnd(startEndStr);
        const start = startMin || timeToMinutes(shiftStart);
        const end = endMin || timeToMinutes(shiftEnd);
        if (end <= start) return { positionX: 0, positionW: 1 };
        const positionX = Math.max(0, Math.floor((start - gridStartMin) / 30));
        const positionW = Math.max(1, Math.floor((end - start) / 30));
        return { positionX, positionW };
      };

      const positionItems = dedupePartnerDayShiftPositionItems(
        items.filter(
          (it: any) => it.positionId != null && it.positionId !== "",
        ),
      );
      const byDayShift: Record<string, any[]> = {};
      positionItems.forEach((it: any) => {
        const key = `${it.day}|${it.shift}`;
        if (!byDayShift[key]) byDayShift[key] = [];
        byDayShift[key].push(it);
      });

      const existingByDayShift: Record<string, any[]> = {};
      schedulesForSave.forEach((s: any) => {
        if (removedScheduleIds.has(Number(s?.id))) return;
        const day = s.dayOfWeek;
        const sn = normalizeShiftNameForSave(s.shift);
        if (!day || !sn) return;
        const key = `${day}|${sn}`;
        if (!existingByDayShift[key]) existingByDayShift[key] = [];
        existingByDayShift[key].push(s);
      });

      const weekDaysList = [
        "Lunes",
        "Martes",
        "Miercoles",
        "Jueves",
        "Viernes",
        "Sabado",
        "Domingo",
      ];

      for (const day of weekDaysList) {
        const shiftsWithDesiredForPartnerDay = (partnerId: number): Set<string> => {
          const out = new Set<string>();
          shiftNames.forEach((sn) => {
            const items = byDayShift[`${day}|${sn}`] || [];
            if (
              items.some(
                (it: any) => Number(it.partnerId) === Number(partnerId),
              )
            ) {
              out.add(sn);
            }
          });
          return out;
        };

        for (const shiftName of shiftNames) {
          const shiftNum = shiftToNum[shiftName];
          const desiredItems = byDayShift[`${day}|${shiftName}`] || [];
          const existing = existingByDayShift[`${day}|${shiftName}`] || [];
          /** Varias filas pueden compartir el mismo position.id (plaza distinta con mismo catálogo): agrupar y resolver por positionName. */
          const existingByPartnerPositionGroups: Record<string, any[]> = {};
          existing.forEach((s: any) => {
            const pid = s.partner?.id ?? s.partner;
            const posId = s.position?.id ?? s.position;
            if (pid != null && posId != null) {
              const gkey = `${Number(pid)}_${normalizePositionIdKey(posId)}`;
              if (!existingByPartnerPositionGroups[gkey]) {
                existingByPartnerPositionGroups[gkey] = [];
              }
              existingByPartnerPositionGroups[gkey].push(s);
            }
          });
          const findExistingForDesiredItem = (it: any) => {
            const gkey = `${Number(it.partnerId)}_${normalizePositionIdKey(it.positionId)}`;
            const candidates = existingByPartnerPositionGroups[gkey] || [];
            const want = normalizePositionLabelForSave(
              it.positionDisplayName || it.positionName || "",
            );
            if (candidates.length === 0) {
              return existing.find(
                (s: any) =>
                  Number(s.partner?.id ?? s.partner) === Number(it.partnerId) &&
                  Number(s.position?.id ?? s.position) === Number(it.positionId),
              );
            }
            if (candidates.length === 1) return candidates[0];
            const byName = candidates.find(
              (s: any) =>
                normalizePositionLabelForSave(s.positionName || "") === want,
            );
            return byName || candidates[0];
          };

          const { startTime: startRaw, endTime: endRaw } = getStartEndForShift(shiftName);
          const exactDateObj = getDateByDayOfWeek(weekValue, day);
          const exactDateStr = exactDateObj
            ? `${exactDateObj.getFullYear()}-${String(exactDateObj.getMonth() + 1).padStart(2, "0")}-${String(exactDateObj.getDate()).padStart(2, "0")}`
            : "";
          const timeRangeStr =
            startRaw && endRaw ? `${startRaw} - ${endRaw}` : "";

          const formatPartTime = (t: string) =>
            String(t ?? "").trim().length >= 5 ? String(t).trim().slice(0, 5) : String(t ?? "").trim();
          const firstOfRange = (r: string) => {
            const str = String(r ?? "").trim();
            if (!str) return "";
            if (str.includes(" - ")) return formatPartTime(str.split(" - ")[0]?.trim() || str);
            return formatPartTime(str);
          };
          const lastOfRange = (r: string) => {
            const str = String(r ?? "").trim();
            if (!str) return "";
            if (str.includes(" - ")) {
              const parts = str.split(" - ").map((x: string) => x.trim());
              return formatPartTime(parts[parts.length - 1] || parts[0] || str);
            }
            return formatPartTime(str);
          };
          const toTimeRange = (s: string, e: string) => {
            if (!s || !e) return s && e ? "" : (s || e || "");
            const inicio = firstOfRange(s);
            const fin = lastOfRange(e);
            if (!inicio || !fin) return inicio && fin ? "" : (inicio || fin || "");
            return `${inicio} - ${fin}`;
          };

          const scheduleRecordsFromDesired: any[] = desiredItems.map((it: any) => {
            const existingRecord = (() => {
              if (it.branchScheduleId != null && it.branchScheduleId !== undefined) {
                const bid = Number(it.branchScheduleId);
                const found = schedulesForSave.find(
                  (s: any) => Number(s.id) === bid,
                );
                // Solo enlazar por id si el registro es del mismo turno que este batch;
                // si no, createSchedule no encuentra la fila en oldRecords y falla al actualizar.
                if (
                  found &&
                  normalizeShiftNameForSave(found.shift) ===
                    normalizeShiftNameForSave(shiftName)
                ) {
                  return found;
                }
              }
              return findExistingForDesiredItem(it);
            })();
            const startTime = existingRecord
              ? toTimeRange(
                  existingRecord.startTime ?? startRaw,
                  existingRecord.endTime ?? endRaw
                ) || timeRangeStr
              : timeRangeStr;
            const endTime = existingRecord
              ? toTimeRange(
                  existingRecord.startTime ?? startRaw,
                  existingRecord.endTime ?? endRaw
                ) || timeRangeStr
              : timeRangeStr;
            const { positionX: posX, positionW: posW } = getPositionXW(
              startTime || timeRangeStr,
              startRaw,
              endRaw,
            );
            const positionNameForGrid =
              it.positionDisplayName || it.positionName || "";
            if (existingRecord) {
              return {
                dateRange: weekValue,
                dayOfWeek: day,
                positionName: positionNameForGrid,
                positionX: posX,
                positionW: posW,
                positionY: existingRecord.positionY ?? 0,
                startTime,
                endTime,
                exactDate: exactDateStr,
                branch: selectedBranchId,
                partner: it.partnerId,
                position: it.positionId,
                shift: shiftName,
                positionOriginalId_: existingRecord.positionOriginalId ?? it.positionId,
                originalRecordId: existingRecord.id,
              };
            }
            return {
              dateRange: weekValue,
              dayOfWeek: day,
              positionName: positionNameForGrid,
              positionX: posX,
              positionW: posW,
              positionY: 0,
              startTime: timeRangeStr,
              endTime: timeRangeStr,
              exactDate: exactDateStr,
              branch: selectedBranchId,
              partner: it.partnerId,
              position: it.positionId,
              shift: shiftName,
              positionOriginalId_: it.positionId,
            };
          });

          const desiredPartnerPositionKeys = new Set<string>();
          desiredItems.forEach((it: any) => {
            if (it.branchScheduleId != null && it.branchScheduleId !== undefined) {
              const bid = Number(it.branchScheduleId);
              const byId = schedulesForSave.find(
                (s: any) => Number(s.id) === bid,
              );
              if (
                byId &&
                normalizeShiftNameForSave(byId.shift) ===
                  normalizeShiftNameForSave(shiftName)
              ) {
                desiredPartnerPositionKeys.add(`sid:${bid}`);
              }
            }
            desiredPartnerPositionKeys.add(
              `${Number(it.partnerId)}_${normalizePositionIdKey(it.positionId)}_${normalizePositionLabelForSave(it.positionDisplayName || it.positionName || "")}`,
            );
          });
          const preservedRecords: any[] = existing
            .filter((existingRecord: any) => {
              const pid = existingRecord.partner?.id ?? existingRecord.partner;
              const posId =
                existingRecord.position?.id ?? existingRecord.position;
              if (pid == null) return false;
              const eid = Number(existingRecord.id);
              if (!Number.isNaN(eid) && eid > 0 && desiredPartnerPositionKeys.has(`sid:${eid}`)) {
                return false;
              }
              const desiredSlotKey = `${Number(pid)}_${normalizePositionIdKey(posId)}_${normalizePositionLabelForSave(existingRecord.positionName || "")}`;
              if (posId != null && desiredPartnerPositionKeys.has(desiredSlotKey)) {
                return false;
              }
              const recordShift = normalizeShiftNameForSave(
                existingRecord.shift,
              );
              const desiredShiftsForPartner = shiftsWithDesiredForPartnerDay(pid);
              if (
                desiredShiftsForPartner.size > 0 &&
                !desiredShiftsForPartner.has(recordShift)
              ) {
                return false;
              }
              const cellKey = `${pid}-${day}`;
              const init = (weeklyCtx.initialCellAssignments[cellKey] ?? "").trim();
              const curr = (weeklyCtx.cellAssignments[cellKey] ?? "").trim();
              if (curr === "DESCANSO") return false;
              const wasAssigned =
                !!init && init !== "-" && init !== "DESCANSO";
              const nowEmpty = !curr || curr === "-";
              if (wasAssigned && nowEmpty) return false;
              return true;
            })
            .map((existingRecord: any) => ({
              dateRange: weekValue,
              dayOfWeek: day,
              positionName: existingRecord.positionName,
              positionX: Number(existingRecord.positionX) || 0,
              positionW: Number(existingRecord.positionW) || 1,
              positionY: existingRecord.positionY ?? 0,
              startTime: existingRecord.startTime,
              endTime: existingRecord.endTime,
              exactDate: exactDateStr,
              branch: selectedBranchId,
              partner: existingRecord.partner?.id ?? existingRecord.partner,
              position:
                existingRecord.position?.id ?? existingRecord.position,
              shift: shiftName,
              positionOriginalId_:
                existingRecord.originalPositionId ??
                existingRecord.position?.id ??
                existingRecord.position,
              originalRecordId: existingRecord.id,
            }));

          const scheduleRecords = dedupeScheduleRecordsForSave([
            ...scheduleRecordsFromDesired,
            ...preservedRecords,
          ]);

          if (!exactDateStr) continue;
          await saveSchedule({
            branchId: selectedBranchId,
            schedule: scheduleRecords,
            exactDate: exactDateStr,
            shift: shiftNum,
            allowDuplicatePartnerPosition: false,
          });
        }
      }

      await fetchDependencies(selectedBranchId, formik.values.week);
      setHasUnsavedChanges(false);

      notifyBranchSchedulesSaved(
        selectedBranchId,
        formik.values.week != null ? Number(formik.values.week) : undefined,
      );

      notification(
        "Éxito",
        "Horario semanal guardado correctamente",
        "success",
      );
    } catch (error: any) {
      console.error("Error saving schedule:", error);
      notification(
        "Error",
        error.response?.data?.message || "Error al guardar el horario semanal",
        "danger",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleFillWeek = () => {
    if (!tableRef.current) {
      notification(
        "Error",
        "No se pudo obtener los datos de la tabla",
        "danger",
      );
      return;
    }

    const result = tableRef.current.fillWeekWithCurrentDay();
    if (result) {
      notification(
        "Éxito",
        "Horario rellenado automáticamente con colaboradores y puestos que hacen match. No olvides guardar los cambios.",
        "success",
      );
    } else {
      notification(
        "Info",
        "No se encontraron colaboradores que hagan match con las posiciones",
        "info",
      );
    }
  };

  const handleSyncWithSchedule = async () => {
    if (!tableRef.current) {
      notification(
        "Error",
        "No se pudo obtener los datos de la tabla",
        "danger",
      );
      return;
    }

    setSaving(true);
    try {
      const weekValue = weeks[formik.values.week]?.value;
      if (!weekValue) {
        notification("Error", "Semana no válida", "danger");
        return;
      }

      const schedules = await readSchedulesByBranchAndWeek(
        selectedBranchId,
        weekValue,
      );

      if (!schedules || schedules.length === 0) {
        notification(
          "Info",
          "No se encontraron horarios configurados para esta semana",
          "info",
        );
        return;
      }

      const result = tableRef.current.syncWeekWithSchedule(schedules);
      if (!result) {
        notification(
          "Info",
          "No se pudieron sincronizar horarios para las posiciones actuales",
          "info",
        );
        return;
      }

      const weekStart = getDateByDayOfWeek(weekValue, "Lunes");
      const weekEnd = getDateByDayOfWeek(weekValue, "Domingo");

      if (!weekStart || !weekEnd) {
        notification(
          "Error",
          "Error al obtener las fechas de la semana",
          "danger",
        );
        return;
      }

      const tableData = tableRef.current.getTableData();
      const { items, restDays } = tableData;

      await saveBranchScheduleWeekend({
        branchId: selectedBranchId,
        weekValue: weekValue,
        weekStart: moment(weekStart).format("YYYY-MM-DD"),
        weekEnd: moment(weekEnd).format("YYYY-MM-DD"),
        items: items,
        restDays: restDays,
      });

      await fetchDependencies(selectedBranchId, formik.values.week);

      notification(
        "Éxito",
        "Semana sincronizada y guardada con los horarios diarios",
        "success",
      );
    } catch (error: any) {
      console.error("Error syncing schedule:", error);
      notification(
        "Error",
        error.response?.data?.message ||
          "Error al sincronizar con los horarios",
        "danger",
      );
    } finally {
      setSaving(false);
    }
  };

  const weekDays = [
    "Lunes",
    "Martes",
    "Miercoles",
    "Jueves",
    "Viernes",
    "Sabado",
    "Domingo",
  ];

  const openNotesModal = () => {
    setWeekNotes(weekendSchedule?.notes ?? "");
    setShowNotesModal(true);
  };

  const handleSaveNotes = async () => {
    const weekData = weeks[formik.values.week];
    const weekValue = weekData?.value;
    if (!weekValue || !selectedBranchId) return;
    setSavingNotes(true);
    try {
      if (weekendSchedule?.id) {
        await updateBranchScheduleWeekend(weekendSchedule.id, { notes: weekNotes });
        setWeekendSchedule((prev: any) => (prev ? { ...prev, notes: weekNotes } : null));
      } else {
        const start = weekData?.exactDates?.start;
        const end = weekData?.exactDates?.end;
        if (!start || !end) {
          notification("Aviso", "No se pudo obtener el rango de la semana", "warning");
          return;
        }
        const weekStart = moment(start).format("YYYY-MM-DD");
        const weekEnd = moment(end).format("YYYY-MM-DD");
        const created = await saveBranchScheduleWeekend({
          branchId: selectedBranchId,
          weekValue,
          weekStart,
          weekEnd,
          items: [],
          restDays: [],
          notes: weekNotes || undefined,
        });
        setWeekendSchedule(created);
      }
      notification("Listo", "Notas guardadas correctamente", "success");
      setShowNotesModal(false);
    } catch (e: any) {
      notification("Error", e?.message || "Error al guardar las notas", "danger");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDownloadTablePdf = async () => {
    if (!tableRef.current) {
      notification("Error", "No se pudo obtener los datos de la tabla", "danger");
      return;
    }
    setExportingTablePdf(true);
    try {
      await tableRef.current.exportCurrentTableAsPdf({
        branchName: branchName || "Sucursal",
        weekName: weeks[formik.values.week]?.name || "Semana",
        notes: (weekendSchedule?.notes ?? weekNotes) || undefined,
      });
      notification("Éxito", "Tabla descargada como PDF correctamente", "success");
    } catch (error: any) {
      console.error("Error exporting table PDF:", error);
      notification("Error", error?.message || "Error al descargar la tabla en PDF", "danger");
    } finally {
      setExportingTablePdf(false);
    }
  };

  const handleDayClick = (
    day: string,
    positionName?: string,
    positionShift?: string,
  ) => {
    const dayIndex = weekDays.indexOf(day);
    navigate(`/sucursales/horario/${selectedBranchId}`, {
      state: {
        dayOfWeek: dayIndex,
        week: formik.values.week,
        shift: 3,
        positionName,
        positionShift,
      },
    });
  };

  return (
    <Layout>
      <Title
        baseTitle="Configuración"
        basePath=""
        title="Configurar Horario Semanal"
      />

      <ConfigHorarioSemanalFilters
        formik={formik}
        branchId={selectedBranchId}
        initialBranchId={id}
        onBranchChange={(newBranchId) => {
          setSelectedBranchId(newBranchId);
        }}
        canChangeBranch={user.rol !== "Lider de sucursal"}
        onFilterChange={() => {}}
        branchName={branchName}
        onExportClick={() => setShowExportModal(true)}
        onSaveClick={handleSave}
        onFillWeekClick={handleFillWeek}
        onSyncWithScheduleClick={handleSyncWithSchedule}
        saving={saving}
        hasUnsavedChanges={hasUnsavedChanges}
        onRefreshClick={() =>
          fetchDependencies(selectedBranchId, formik.values.week)
        }
        onNotesClick={openNotesModal}
        onDownloadTablePdf={handleDownloadTablePdf}
        exportingTablePdf={exportingTablePdf}
      />

      {loading ? (
        <Loader isLoading={loading} />
      ) : (
        <ConfigHorarioSemanalTable
          ref={tableRef}
          positions={positions}
          schedules={schedules}
          partners={partners}
          weekDays={weekDays}
          weekValue={weeks[formik.values.week]?.value}
          branchId={selectedBranchId}
          availableRoles={availableRoles}
          freeDays={freeDays}
          weekendSchedule={weekendSchedule}
          branchHours={branchHours}
          scheduleViewMode={true}
          onDayClick={handleDayClick}
          onUnsavedChange={setHasUnsavedChanges}
        />
      )}

      <ConfigHorarioSemanalExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        positions={positions}
        schedules={schedules}
        partners={partners}
        weekDays={weekDays}
        weekValue={weeks[formik.values.week]?.value || ""}
        branchName={branchName}
        weekName={weeks[formik.values.week]?.name || ""}
      />

      {showNotesModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.4)",
          }}
          onClick={() => !savingNotes && setShowNotesModal(false)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
              padding: "24px",
              minWidth: "400px",
              maxWidth: "90vw",
              maxHeight: "80vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", fontSize: "18px", fontWeight: 600, color: "#111827" }}>
              Notas de la semana
            </h3>
            <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#6B7280" }}>
              {weeks[formik.values.week]?.name || "Semana actual"}
            </p>
            <textarea
              value={weekNotes}
              onChange={(e) => setWeekNotes(e.target.value)}
              placeholder="Escribe notas para esta semana..."
              rows={5}
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "14px",
                border: "1px solid #D1D5DB",
                borderRadius: "8px",
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "16px" }}>
              <button
                type="button"
                onClick={() => !savingNotes && setShowNotesModal(false)}
                style={{
                  padding: "10px 18px",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#6B21A8",
                  backgroundColor: "#F3E8FF",
                  border: "1px solid #E9D5FF",
                  borderRadius: "8px",
                  cursor: savingNotes ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!savingNotes) {
                    e.currentTarget.style.backgroundColor = "#EDE9FE";
                    e.currentTarget.style.borderColor = "#C4B5FD";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#F3E8FF";
                  e.currentTarget.style.borderColor = "#E9D5FF";
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveNotes}
                disabled={savingNotes}
                style={{
                  padding: "10px 18px",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1D4ED8",
                  backgroundColor: savingNotes ? "#E0E7FF" : "#DBEAFE",
                  border: "1px solid #93C5FD",
                  borderRadius: "8px",
                  cursor: savingNotes ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!savingNotes) {
                    e.currentTarget.style.backgroundColor = "#BFDBFE";
                    e.currentTarget.style.borderColor = "#60A5FA";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = savingNotes ? "#E0E7FF" : "#DBEAFE";
                  e.currentTarget.style.borderColor = "#93C5FD";
                }}
              >
                {savingNotes ? "Guardando..." : "Guardar notas"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ConfigHorarioSemanal;
